import React, { useState, FC, Children, useRef, useCallback } from 'react';
import SwiperType from 'swiper';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import 'swiper/css/navigation'; // eslint-disable-line import/no-unresolved
import 'swiper/css/pagination'; // eslint-disable-line import/no-unresolved
import 'swiper/css/a11y'; // eslint-disable-line import/no-unresolved
import 'swiper/css/autoplay'; // eslint-disable-line import/no-unresolved
import { SliderNavigationProps } from 'components/commercetools-ui/atoms/slider/condensed-banner-slider-navigation';
import { ArrowLeft } from 'components/icons';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';

export type SliderProps = SliderNavigationProps & {
  uniqueFFIDText?: string;
  slidesPerView?: number;
} & SwiperProps;

const HeaderNavigationSlider: FC<SliderProps> = ({
  slidesPerView,
  children,
  onSwiper,
  onInit,
  allowTouchMove,
  uniqueFFIDText,
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();
  const [init, setInit] = useState(false);

  // Track boundary states natively
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const navigationPrevRef = useRef<HTMLButtonElement>(null);
  const navigationNextRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<SwiperType>(undefined);

  const handleInit = useCallback(
    (swiper: SwiperType) => {
      setInit(true);
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
      setIsLocked(swiper.isLocked);
      onInit?.(swiper);
    },
    [onInit],
  );

  const containerClassNames = useClassNames([
    'flex w-full mx-auto h-full slider_container relative',
    init ? 'visible' : 'invisible',
    {
      'slider__container--fit': true,
    },
  ]);

  const handleSlideMouseEnter = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const swiper = swiperRef.current;
    if (!swiper) return;

    const slideElement = event.currentTarget as HTMLElement;
    const slideRect = slideElement.getBoundingClientRect();
    const swiperElement = swiper.el;
    if (!swiperElement) return;

    const swiperRect = swiperElement.getBoundingClientRect();
    const isFullyVisible = slideRect.left >= swiperRect.left && slideRect.right <= swiperRect.right;
    if (isFullyVisible) return;

    let moved = false;

    if (slideRect.left < swiperRect.left) {
      swiper.slidePrev(200);
      moved = true;
    } else if (slideRect.right > swiperRect.right) {
      swiper.slideNext(200);
      moved = true;
    }

    if (moved) {
      setTimeout(() => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
        setIsLocked(swiper.isLocked);
        swiper.navigation.update();
      }, 200);
    }
  }, []);

  const childrenCount = Children.count(children);
  const slides = Children.map(children, (child, index) => {
    const isFirst = index === 0;
    const isLast = index === childrenCount - 1;

    return (
      <SwiperSlide
        className="!w-fit"
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: isFirst ? '18px' : undefined,
          paddingRight: isLast ? '18px' : undefined,
        }}
        onMouseEnter={(event) => handleSlideMouseEnter(event)}
      >
        {child}
      </SwiperSlide>
    );
  });

  const handleOnSwiper = (swiper: SwiperType) => {
    swiperRef.current = swiper;

    const updateStates = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
      setIsLocked(swiper.isLocked);
    };

    // Hooked directly into standard initialization progress changes
    swiper.on('progress', updateStates);
    swiper.on('slideChange', updateStates);
    swiper.on('resize', updateStates);
    swiper.on('lock', () => setIsLocked(true));
    swiper.on('unlock', () => setIsLocked(false));

    onSwiper?.(swiper);
  };

  const showPrevArrow = !isLocked && !isBeginning;
  const showNextArrow = !isLocked && !isEnd;

  return (
    <div className={containerClassNames}>
      <button
        ref={navigationPrevRef}
        className={`mr-10 cursor-pointer ${showPrevArrow ? '' : 'hidden'}`}
        data-ffid="btn-prev-ctr-cat-menu-slider"
      >
        <span>
          <ArrowLeft className="text-black" dimension={16} data-ffid="icon-prev-ctr-cat-menu-slider" />
        </span>
      </button>

      <Swiper
        key={`${allowTouchMove} ${isTouchDevice}`}
        className="slider min-w-0 flex-1"
        modules={[Navigation, Pagination, Autoplay, A11y]}
        pagination={false}
        slidesPerView={slidesPerView ?? 'auto'}
        spaceBetween={8}
        style={{ width: '100%' }}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onSwiper={handleOnSwiper}
        watchSlidesProgress={true}
        observer={true}
        observeParents={true}
        onAfterInit={(swiper) => {
          setTimeout(() => {
            swiper.update();
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
            setIsLocked(swiper.isLocked);
          }, 100); // Small bump to 100ms guarantees DOM nodes have completely settled widths
        }}
        onInit={handleInit}
        allowTouchMove={allowTouchMove && isTouchDevice}
        preventClicks={false}
        preventClicksPropagation={false}
        data-ffid={`ctr-swiper${uniqueFFIDText ? '-' + uniqueFFIDText : ''}`}
        {...props}
        a11y={{
          enabled: true,
          slideRole: 'none',
        }}
      >
        {slides}
      </Swiper>

      <button
        ref={navigationNextRef}
        className={`ml-10 cursor-pointer ${showNextArrow ? '' : 'hidden'}`}
        data-ffid="btn-next-ctr-cat-menu-slider"
      >
        <span>
          <ArrowLeft className="rotate-180 text-black" dimension={16} data-ffid="icon-next-ctr-cat-menu-slider" />
        </span>
      </button>
    </div>
  );
};

export default HeaderNavigationSlider;
